import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ToggleFilterMode, isAllSelected, isolate, selectAll, toggle } from './toggle-filter.logic';

export interface ToggleFilterOption { value: string; label: string }

/**
 * Option filter. `multi`: independent toggles (like chart legend items), derived "All",
 * double-click isolates, last option locked on. `single-or-all`: exactly one option or All.
 */
@Component({
  selector: 'cosmos-toggle-filter',
  imports: [ButtonModule, TooltipModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-wrap items-center gap-2" role="group">
      <p-button
        size="small"
        [label]="'toggle_filter.all' | translate"
        [outlined]="!allSelected()"
        [severity]="allSelected() ? 'primary' : 'secondary'"
        [attr.aria-pressed]="allSelected()"
        (onClick)="onAll()" />
      @for (o of options(); track o.value) {
        <p-button
          size="small"
          [label]="o.label"
          [outlined]="!isSelected(o.value)"
          [severity]="isSelected(o.value) ? 'primary' : 'secondary'"
          [attr.aria-pressed]="isSelected(o.value)"
          [pTooltip]="mode() === 'multi' ? ('toggle_filter.isolate_hint' | translate) : ''"
          tooltipPosition="bottom"
          (onClick)="onToggle(o.value)"
          (dblclick)="onIsolate(o.value)" />
      }
    </div>
  `,
})
export class ToggleFilterComponent {
  readonly options = input.required<ToggleFilterOption[]>();
  readonly mode = input<ToggleFilterMode>('multi');
  readonly selected = model.required<string[]>();

  private readonly allValues = computed(() => this.options().map((o) => o.value));
  readonly allSelected = computed(() => isAllSelected(this.selected(), this.allValues()));

  isSelected(value: string): boolean {
    return this.selected().includes(value);
  }
  onAll(): void {
    this.selected.set(selectAll(this.allValues()));
  }
  onToggle(value: string): void {
    this.selected.set(toggle(this.selected(), value, this.mode(), this.allValues()));
  }
  onIsolate(value: string): void {
    this.selected.set(isolate(value));
  }
}
